/**
 * Article link rules (G2, D8, PR9). href: absolute http(s), a site path "/x" or "#fragment" (anything
 * else — javascript:, protocol-relative, screen ids — is not a link). Target: an explicit
 * linkOptions / ArticleLink target wins; otherwise same tab for paths, fragments and same-site hosts,
 * new tab for other hosts. Same-site = same host or the same last two DNS labels (www.segmently.ai and
 * articles.segmently.ai are one site).
 */
const HTTP = /^https?:\/\/[^\s"'<>\\]+$/i;
const SITE_PATH = /^\/(?![/\\])[^\s"'<>\\]*$/;
const FRAGMENT = /^#[\w-]*$/;

export type ArticleLinkTargetValue = 'same' | 'new';

export function resolveArticleHref(raw: string | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  return HTTP.test(value) || SITE_PATH.test(value) || FRAGMENT.test(value) ? value : null;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

const siteKey = (host: string): string => host.split('.').slice(-2).join('.');

export function isSameSite(href: string, siteOrigin: string | undefined): boolean {
  if (!HTTP.test(href)) return true;
  const host = hostOf(href);
  const site = siteOrigin ? hostOf(siteOrigin) : null;
  if (!host || !site) return false;
  return host === site || siteKey(host) === siteKey(site);
}

export function articleLinkOpensNewTab(href: string, target: string | undefined, siteOrigin: string | undefined): boolean {
  if (target === 'new') return true;
  if (target === 'same') return false;
  return !isSameSite(href, siteOrigin);
}

export function articleLinkTargetProps(href: string, target: string | undefined, siteOrigin: string | undefined): { target?: '_blank'; rel?: string } {
  return articleLinkOpensNewTab(href, target, siteOrigin) ? { target: '_blank', rel: 'noopener noreferrer' } : {};
}
